import React from 'react';
import { Button, DatePicker, Flex, Form, Layout, Modal } from 'antd';
import makeApiRequest from '@src/apiCalls/makeApiRequest';
import { API_ENDPOINTS } from '@src/constants/apiEndpoints';
import apiRequestConfig from '@src/interfaces/apiEndpoints.interface';
import { useNotification } from '@src/providers/NotificationProviders';

interface DownloadModelProps {
  title: string;
  visible: boolean;
  setVisible: (visible: boolean) => void;
  verificationType: string;
}
const CostcoDashboardDownloadModal: React.FC<DownloadModelProps> = ({
  title,
  visible,
  setVisible,
  verificationType,
}) => {
  const openNotification = useNotification();
  const [form] = Form.useForm();
  const token = localStorage.getItem('costcoAdminToken');

  const handleCancel = () => {
    setVisible(false);
    form.resetFields();
  };
  const extractTitleAfterDownload = (title: string) => {
    const extractedTitle = title.replace(/^Export\s+/i, '').trim();
    return extractedTitle;
  };
  const handleDownload = (values: any) => {
    form
      .validateFields()
      .then((values) => {
        const { fromTime, toTime, status } = values;

        const params = {
          fromTime: fromTime ? fromTime.unix() : null,
          toTime: toTime ? toTime.unix() : null,
        };

        const extractedTitle = extractTitleAfterDownload(title);

        const queryParams = new URLSearchParams({
          startTime: params.fromTime?.toString() || '',
          endTime: params.toTime?.toString() || '',
          timezone: "America/New_York",
          verification_type: verificationType,
        });

        if (status && status.trim() !== '') {
          queryParams.append('status', status);
        }

        makeApiRequest(
          {},
          API_ENDPOINTS.DOWNLOAD_COSTCO_DASHBOARD_REQUEST as apiRequestConfig,
          `?${queryParams.toString()}`,
          {
            Authorization: `Bearer ${token}`,
          }
        )
          .then((response) => {
            const csvData = response.data.trim();
            const lines = csvData.split('\n');

            if (lines.length <= 1) {
              openNotification('No Data Found', 'Error');
            } else {
              const blob = new Blob([csvData], { type: 'text/csv' });
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `${extractedTitle}_${params.fromTime}_${params.toTime}.csv`;
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
              window.URL.revokeObjectURL(url);
              openNotification('Export Successful', 'Success');
            }
            form.resetFields();
            setVisible(false);
          })
          .catch(() => {
            openNotification('Failed to Export', 'Error');
            setVisible(false);
            form.resetFields();
          });
      })
      .catch(() => {
        openNotification('Please fill all the required fields', 'Error');
      });
  };

  return (
    <Modal
      title={title}
      open={visible}
      onCancel={handleCancel}
      footer={[
        <Flex
          key="footer-buttons"
          gap={8}
          style={{ justifyContent: 'flex-end' }}
        >
          <Button key="clear" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            key="apply"
            type="primary"
            htmlType="submit"
            onClick={handleDownload}
          >
            Export
          </Button>
        </Flex>,
      ]}
    >
      <Layout style={{ marginTop: '20px' }}>
        <Form form={form} layout="vertical" style={{ background: 'white' }}>
          <Form.Item
            label="From Date & Time"
            name="fromTime"
            rules={[
              { required: true, message: 'From Date & Time is required' },
            ]}
          >
            <DatePicker
              showTime
              format="YYYY-MM-DD HH:mm:ss"
              placeholder="Select From Time"
            />
          </Form.Item>
          <Form.Item
            label="To Date & Time"
            name="toTime"
            rules={[{ required: true, message: 'To Date & Time is required' }]}
          >
            <DatePicker
              showTime
              format="YYYY-MM-DD HH:mm:ss"
              placeholder="Select To Time"
            />
          </Form.Item>
        </Form>
      </Layout>
    </Modal>
  );
};
export default CostcoDashboardDownloadModal;
